import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Eye, ArrowLeft, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useStore } from '@/contexts/StoreContext';
import { apiClient } from '@/lib/api/client';
import { Post } from '@/hooks/usePosts';
import { toast } from 'sonner';
import BlogCard from '@/components/blog/BlogCard';
import { useAvatar } from '@/hooks/useAvatar';
import { sanitizeHtml } from '@/lib/sanitize';
import { useTranslation } from 'react-i18next';

import { Caixa } from '@/construtor/caixa';
import { EspacoDeAnuncio } from '@/templates/comum/anuncios/EspacoDeAnuncio';
export default function NoticiaPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { store } = useStore();
  const { getAvatarUrl } = useAvatar();
  const [post, setPost] = useState<Post | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!store?.id || !slug) return;

      try {
        setLoading(true);
        
        // Fetch the specific post
        const postResponse = await apiClient.get<Post>(
          `/stores/${store.id}/posts/published/${slug}`
        );
        setPost(postResponse);
        
        // Fetch related posts
        const listResponse = await apiClient.get<{ posts: Post[] }>(
          `/stores/${store.id}/posts/published?limit=4`
        );
        
        /*
          Compara com o **id da notícia carregada**, não com o parâmetro da
          URL. O parâmetro agora é o slug: comparar com ele nunca casaria, e a
          própria notícia apareceria na lista de relacionadas dela mesma.
        */
        const related = listResponse.posts
          .filter((p: Post) => p.id !== postResponse.id)
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (err: any) {
        toast.error(t('store.news.load_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [store?.id, slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto animate-pulse">
          <div className="h-96 bg-muted rounded-lg mb-8"></div>
          <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <Caixa id="noticia.nao-encontrada">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-display text-4xl font-bold text-foreground mb-4">
            {t('store.news.not_found_title')}
          </h1>
          <p className="text-muted-foreground mb-8">
            {t('store.news.not_found_description')}
          </p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('store.news.back')}
          </Button>
        </div>
        </Caixa>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-16">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Caixa id="noticia.voltar">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('store.news.back_button')}
          </Button>
        </div>
        </Caixa>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Caixa id="noticia.artigo" miolo>
            <Card className="bg-card border-border overflow-hidden">
              {/* Header Image */}
              <Caixa id="noticia.capa">
              <div className="relative h-96 overflow-hidden">
                <img
                  src={post.bannerUrl || 'https://cdn.hystore.com.br/hystore/11.jpg'}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                
                {/* Category Badge */}
                <Badge 
                  className="absolute top-4 left-4 bg-primary/90 text-primary-foreground"
                >
                  {t('store.news.category')}
                </Badge>
              </div>
              </Caixa>

              <CardContent className="p-8">
                {/* Title */}
                <Caixa id="noticia.titulo">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {post.title}
                </h1>
                </Caixa>

                {/* Meta Info */}
                <Caixa id="noticia.dados">
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{post.authorNick}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.createdAt)}</span>
                  </div>
                </div>
                </Caixa>

                {/* Content */}
                <Caixa id="noticia.texto">
                <div 
                  className="prose prose-lg dark:prose-invert max-w-none
                    prose-headings:font-display prose-headings:text-foreground
                    prose-p:text-muted-foreground
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-foreground
                    prose-img:rounded-xl
                    prose-pre:bg-muted prose-pre:border prose-pre:border-border
                    prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  "
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
                />
                </Caixa>
              </CardContent>
            </Card>
            </Caixa>
            <EspacoDeAnuncio posicao="ARTICLE_END" className="mt-6" />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Caixa id="noticia.lateral">
            <div className="sticky top-24 space-y-6">
              {/* Autor da Notícia */}
              <Caixa id="noticia.autor">
              <div className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6">
                <h3 className="font-display text-lg font-bold text-foreground mb-4">
                  {t('store.news.posted_by')}
                </h3>
                <div className="flex items-start gap-4">
                  <img
                    src={getAvatarUrl(post.authorNick, 128)}
                    alt={post.authorNick}
                    className="w-16 h-16 rounded-full border-2 border-primary/50 shadow-lg flex-shrink-0"
                    style={{ imageRendering: 'pixelated' }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-display text-lg font-bold text-foreground mb-1">
                      {post.authorNick}
                    </h4>
                    <Badge className="bg-primary/20 text-primary border-primary/30 mb-2">
                      {t('store.news.team')}
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      {t('store.news.author_description')}
                    </p>
                  </div>
                </div>
              </div>
              </Caixa>

              {/* Related News */}
              {relatedPosts.length > 0 && (
                <Caixa id="noticia.relacionadas">
                <div className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6">
                  <h3 className="font-display text-xl font-bold text-foreground mb-4">
                    {t('store.news.related')}
                  </h3>
                  <div className="space-y-4">
                    {relatedPosts.map((relatedPost) => (
                      <Caixa id="noticia.relacionadas.item" repetida key={relatedPost.id}>
                      <Link
                        key={relatedPost.id}
                        to={`/noticias/${relatedPost.slug ?? relatedPost.id}`}
                        className="block group"
                      >
                        <div className="flex gap-3 p-3 rounded-lg hover:bg-background/50 transition-colors">
                          <img
                            src={relatedPost.bannerUrl || 'https://cdn.hystore.com.br/hystore/11.jpg'}
                            alt={relatedPost.title}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                              {relatedPost.title}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(relatedPost.createdAt)}
                            </p>
                          </div>
                        </div>
                      </Link>
                      </Caixa>
                    ))}
                  </div>
                </div>
                </Caixa>
              )}

              {/* Share */}
              <div className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6" data-testid="share-section">
                <h3 className="font-display text-lg font-bold text-foreground mb-4">
                  {t('store.news.share')}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    data-testid="copy-link-button"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                    }}
                  >
                    {t('store.news.copy_link')}
                  </Button>
                </div>
              </div>
            </div>
            </Caixa>
          </div>
        </div>
      </div>
    </div>
  );
}
